import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The starting tree (SPEC-008 PR-8.12).
 *
 * A drag strip does not begin with a button; it begins with a tree — two ambers
 * and a green, and the wait between them is the whole reason a standing start
 * feels like anything. This is that wait, and it is the one part of the race
 * that is deliberately NOT physics: the cars have not moved yet and nothing
 * about the result depends on it.
 *
 * It lives in `application/motion/` beside `useRaceClock` for the same reason
 * that does: it is a browser timer bound to a sequence, the sequence is the only
 * interesting part, and the timers are injected so a test can walk it stage by
 * stage rather than wait two seconds per assertion.
 *
 * Under REDUCED MOTION there is no tree at all. A reader who has asked for less
 * movement has not asked to be made to wait for it, so `start` goes straight to
 * `go` and the race begins — which is the same trade the splash makes (SPEC-001)
 * and the same one `useRaceClock` makes for the run itself.
 */

/**
 * The stages, in order, and how long each is held.
 *
 * Two ambers rather than the full five-amber pro tree: five is a professional
 * sequence a reader would sit through rather than watch, and this one has to be
 * over before it becomes a toll on running a second race.
 */
export const LIGHT_STAGES = ['idle', 'set', 'ready', 'go'];

const STAGE_MILLISECONDS = 520;

/**
 * The default timers, at MODULE scope rather than inline in the parameter list.
 *
 * A default parameter expression is evaluated afresh on every call, so an inline
 * arrow here is a new function identity on every render — which makes `stop` new,
 * which makes `reset` new, which re-fires every effect that depends on it. That
 * is not theoretical: it reset the tree on the render immediately after it was
 * started, so the lights never lit and the race never began. `useRaceClock`
 * hoists its own defaults for the same reason.
 */
const defaultSchedule = (callback, delay) => setTimeout(callback, delay);
const defaultCancel = (handle) => clearTimeout(handle);

export default function useStartLights(onGo, {
    reducedMotion = false,
    schedule = defaultSchedule,
    cancel = defaultCancel,
} = {}) {
    const [stage, setStage] = useState('idle');
    const timer = useRef(null);
    // The callback is held in a ref so the sequence does not restart whenever
    // the parent re-renders — which, during a race, is sixty times a second.
    const go = useRef(onGo);

    go.current = onGo;

    const stop = useCallback(() => {
        if (timer.current !== null) {
            cancel(timer.current);
            timer.current = null;
        }
    }, [cancel]);

    const start = useCallback(() => {
        stop();

        if (reducedMotion) {
            setStage('go');
            go.current();

            return;
        }

        // Each stage schedules the next, rather than three timers set at once:
        // one chain can be cut with one cancel, and three cannot.
        const advance = (index) => {
            setStage(LIGHT_STAGES[index]);

            if (LIGHT_STAGES[index] === 'go') {
                timer.current = null;
                go.current();

                return;
            }

            timer.current = schedule(() => advance(index + 1), STAGE_MILLISECONDS);
        };

        advance(1);
    }, [reducedMotion, schedule, stop]);

    /** Back to a dark tree — a new road is a new start, not a continuation. */
    const reset = useCallback(() => {
        stop();
        setStage('idle');
    }, [stop]);

    useEffect(() => stop, [stop]);

    return { stage, start, reset };
}
